import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { UserPlus, ArrowLeft, Upload, X, Camera, MapPin, Calendar, Mail, Phone, GraduationCap, Award, FileText, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { membersAPI } from '../../services/api';

const POLITICAL_DECLARATION_TEXT = 'I hereby declare that I am not affiliated with any political party/group.';

const arrayFrom = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    return value
      .split(/[,\n]/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
};

const calculateAge = (dob) => {
  if (!dob) return '';
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 0 ? String(age) : '';
};

const approximateDobFromAge = (ageValue) => {
  const ageNumber = Number(ageValue);
  if (Number.isNaN(ageNumber) || ageNumber <= 0) return '';
  const today = new Date();
  const approxDate = new Date(
    today.getFullYear() - ageNumber,
    today.getMonth(),
    today.getDate()
  );
  return approxDate.toISOString().split('T')[0];
};

const defaultFormData = {
  // Personal Information
  firstName: '',
  middleName: '',
  lastName: '',
  age: '',
  email: '',
  phone: '',
  alternatePhone: '',
  dateOfBirth: '',
  gender: '',
  nationality: 'Ethiopian',
  idNumber: '',

  // Address Information
  city: '',
  region: '',
  postalCode: '',
  country: 'Ethiopia',

  // Professional Information
  occupation: '',
  organization: '',
  organizationType: '',
  sectorOther: '',
  positionTitle: '',
  workAddress: '',
  workPhone: '',
  workEmail: '',
  yearsOfExperience: '',

  // Membership Information
  membershipType: 'individual',
  joiningDate: '',
  referralSource: '',
  interests: '',
  skills: '',

  // Photo & Documents
  photo: null,
  photoPreview: null,
  idDocumentType: '',
  idDocumentFile: null,
  idDocumentPreview: null,

  // Training Information
  trainings: [],

  // Emergency Contact
  emergencyContactName: '',
  emergencyContactPhone: '',
  emergencyContactRelationship: '',

  // Additional Information
  bio: '',
  socialMedia: {
    linkedin: '',
    twitter: '',
    facebook: ''
  },
  languages: [],
  availability: '',
  volunteerInterest: false,

  // Accessibility & Supporting Documents
  hasDisability: 'no',
  disabilityTypes: [],
  disabilityOther: '',
  disabilityType: '',
  cvFile: null,
  cvFileName: '',
  cvPreview: null,
  cvRemoved: false,

  // Application Narrative
  shortBiography: '',
  interestInEwhrdn: '',
  womensRightsIssues: '',
  genderEqualityChallenges: '',
  politicalAffiliationDeclaration: false,
  signatureFullName: '',
  emailConfirmation: '',
  additionalField: '',
  regionCity: '',

  memberId: null,
};

const AddMemberContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0;
`;

const CardHeader = styled.div`
  padding: 1.5rem 1.5rem 0 1.5rem;
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 2rem 0 1rem 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PhotoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  border: 2px dashed ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: ${props => props.theme.colors.background.secondary};
`;

const PhotoPreview = styled.div`
  width: 150px;
  height: 150px;
  border-radius: ${props => props.theme.borderRadius.full};
  overflow: hidden;
  border: 3px solid ${props => props.theme.colors.primary};
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemovePhotoButton = styled.button`
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: ${props => props.theme.colors.error};
  color: ${props => props.theme.colors.white};
  border: none;
  border-radius: ${props => props.theme.borderRadius.full};
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const FileUpload = styled.div`
  border: 2px dashed ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease-in-out;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  color: ${props => props.theme.colors.text.primary};
  background-color: ${props => props.theme.colors.background.primary};
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  color: ${props => props.theme.colors.text.primary};
  background-color: ${props => props.theme.colors.background.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
`;

const TrainingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const AddMember = ({
  mode = 'create',
  initialData = null,
  onSubmit,
  onCancel,
  submitLabel,
  secondaryButton,
  allowSaveDraft,
  onFormChange,
  title,
  subtitle,
  showHeader = true,
  memberId = null,
} = {}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(() => ({ ...defaultFormData }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [languageInput, setLanguageInput] = useState('');
  const regionOptions = [
    'Addis Ababa',
    'Afar',
    'Amhara',
    'Benishangul-Gumuz',
    'Central Ethiopia',
    'Dire Dawa',
    'Gambela',
    'Harari',
    'Oromia',
    'Sidama',
    'Somali',
    'South West Ethiopia',
    'Southern Nations, Nationalities, and Peoples',
    'Tigray',
  ];
  const photoInputRef = useRef(null);
  const idDocumentInputRef = useRef(null);
  const cvInputRef = useRef(null);

  const computedTitle = title ?? (mode === 'create' ? 'Add New Member' : 'Complete Member Profile');
  const computedSubtitle = subtitle ?? (mode === 'create' ? 'Complete the form below to register a new member' : 'Review and complete the member information before approval.');

  useEffect(() => {
    if (initialData) {
      const normalizedDisabilityTypes = Array.isArray(initialData.disabilityTypes)
        ? initialData.disabilityTypes
        : arrayFrom(initialData.disability_types || initialData.disabilityType);
      setFormData({
        ...defaultFormData,
        ...initialData,
        age: initialData.age ?? (initialData.dateOfBirth ? calculateAge(initialData.dateOfBirth) : ''),
        sectorOther: initialData.sectorOther ?? initialData.sector_other ?? '',
        regionCity: initialData.regionCity ?? initialData.region_city ?? '',
        disabilityTypes: normalizedDisabilityTypes,
        disabilityType: initialData.disabilityType ?? normalizedDisabilityTypes.join(', '),
        disabilityOther: initialData.disabilityOther ?? initialData.disability_other ?? '',
        shortBiography: initialData.shortBiography ?? initialData.short_biography ?? '',
        interestInEwhrdn: initialData.interestInEwhrdn ?? initialData.interest_in_ewhrdn ?? '',
        womensRightsIssues: initialData.womensRightsIssues ?? initialData.womens_rights_issues ?? '',
        genderEqualityChallenges: initialData.genderEqualityChallenges ?? initialData.challenges_gender_equality ?? '',
        politicalAffiliationDeclaration: (() => {
          const declarationSource = initialData.politicalAffiliationDeclaration ?? initialData.political_affiliation_declaration;
          if (Array.isArray(declarationSource)) {
            return declarationSource.length > 0;
          }
          return Boolean(declarationSource);
        })(),
        signatureFullName: initialData.signatureFullName ?? initialData.signature_full_name ?? '',
        emailConfirmation: initialData.emailConfirmation ?? initialData.email_confirmation ?? initialData.email ?? '',
        cvRemoved: false,
        cvFileName: '',
      });
      setLanguageInput('');
    }
  }, [initialData]);

  useEffect(() => {
    if (typeof onFormChange === 'function') {
      onFormChange(formData);
    }
  }, [formData, onFormChange]);

  useEffect(() => {
    return () => {
      if (formData.idDocumentPreview && typeof formData.idDocumentPreview === 'string' && formData.idDocumentPreview.startsWith('blob:')) {
        URL.revokeObjectURL(formData.idDocumentPreview);
      }
    };
  }, [formData.idDocumentPreview]);
  
  const regions = regionOptions;
  
  const membershipTypes = [
    { value: 'individual', label: 'Individual Member' },
    { value: 'organization', label: 'Organization Member' }
  ];

  const sectorOptions = [
    'Civil Society / NGO',
    'Government',
    'Private Sector',
    'Academia',
    'Media',
    'Other',
  ];
  
  const availableTrainings = [
    'Human Rights Advocacy',
    'Gender-Based Violence Prevention',
    'Digital Security',
    'Legal Aid Training',
    'Community Organizing',
    'Policy Analysis',
    'Research Methods',
    'Media Advocacy',
    'Conflict Resolution',
    'Leadership Development'
  ];

  const disabilityOptions = [
    'Mobility impairment',
    'Visual impairment',
    'Hearing impairment',
    'Speech impairment',
    'Intellectual or learning disability',
    'Psychosocial disability',
    'Chronic illness',
    'Other (please specify)'
  ];
  
  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (name.startsWith('socialMedia.')) {
      const socialKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        socialMedia: {
          ...prev.socialMedia,
          [socialKey]: value
        }
      }));
    } else if (name === 'hasDisability') {
      setFormData(prev => ({
        ...prev,
        hasDisability: value,
        disabilityType: value === 'yes' ? prev.disabilityType : '',
      }));
    } else if (name === 'age') {
      if (!/^\d*$/.test(value)) return;
      setFormData(prev => ({
        ...prev,
        age: value,
        dateOfBirth: value ? (prev.dateOfBirth || approximateDobFromAge(value)) : prev.dateOfBirth,
      }));
    } else if (type === 'file') {
      const file = files && files[0];
      if (!file) return;

      if (name === 'photo') {
        const reader = new FileReader();
        reader.onload = (event) => {
          setFormData(prev => ({
            ...prev,
            photo: file,
            photoPreview: event.target.result
          }));
        };
        reader.readAsDataURL(file);
      } else if (name === 'idDocumentFile') {
        setFormData(prev => {
          if (prev.idDocumentPreview && typeof prev.idDocumentPreview === 'string' && prev.idDocumentPreview.startsWith('blob:')) {
            URL.revokeObjectURL(prev.idDocumentPreview);
          }

          let previewValue = file.name;
          if (file.type && file.type.startsWith('image/')) {
            previewValue = URL.createObjectURL(file);
          }

          return {
            ...prev,
            idDocumentFile: file,
            idDocumentPreview: previewValue
          };
        });
      } else if (name === 'cvFile') {
        setFormData(prev => ({
          ...prev,
          cvFile: file,
          cvFileName: file.name,
          cvPreview: null,
          cvRemoved: false,
        }));
      }
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => {
        const nextValue = type === 'checkbox' ? checked : value;
        const updated = {
          ...prev,
          [name]: nextValue,
        };
        if (name === 'email' && !prev.emailConfirmation) {
          updated.emailConfirmation = nextValue;
        }
        if (name === 'dateOfBirth') {
          updated.age = calculateAge(nextValue);
        }
        if (name === 'organizationType' && nextValue !== 'Other') {
          updated.sectorOther = '';
        }
        if (name === 'regionCity') {
          updated.regionCity = nextValue;
          updated.region = nextValue;
        }
        return updated;
      });
    }
  };
  
  const removePhoto = () => {
    setFormData(prev => ({
      ...prev,
      photo: null,
      photoPreview: null
    }));
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const addLanguage = (language) => {
    const normalized = (language || '').trim();
    if (!normalized) return;
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(normalized)
        ? prev.languages
        : [...prev.languages, normalized]
    }));
  };

  const removeLanguage = (language) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.filter(item => item !== language)
    }));
  };

  const handleLanguageAdd = () => {
    addLanguage(languageInput);
    setLanguageInput('');
  };

  const handleLanguageKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      handleLanguageAdd();
    }
  };

  const removeIdDocument = () => {
    setFormData(prev => {
      if (prev.idDocumentPreview && typeof prev.idDocumentPreview === 'string' && prev.idDocumentPreview.startsWith('blob:')) {
        URL.revokeObjectURL(prev.idDocumentPreview);
      }

      return {
        ...prev,
        idDocumentFile: null,
        idDocumentPreview: null,
      };
    });
    if (idDocumentInputRef.current) {
      idDocumentInputRef.current.value = '';
    }
  };

  const removeCv = () => {
    setFormData(prev => ({
      ...prev,
      cvFile: null,
      cvFileName: '',
      cvPreview: null,
      cvRemoved: true,
    }));
    if (cvInputRef.current) {
      cvInputRef.current.value = '';
    }
  };
  
  const addTraining = (training) => {
    if (!formData.trainings.includes(training)) {
      setFormData(prev => ({
        ...prev,
        trainings: [...prev.trainings, training]
      }));
    }
  };
  
  const removeTraining = (training) => {
    setFormData(prev => ({
      ...prev,
      trainings: prev.trainings.filter(t => t !== training)
    }));
  };

  const toggleDisabilityType = (option) => {
    setFormData(prev => {
      const exists = prev.disabilityTypes.includes(option);
      const nextTypes = exists
        ? prev.disabilityTypes.filter(item => item !== option)
        : [...prev.disabilityTypes, option];
      return {
        ...prev,
        disabilityTypes: nextTypes,
        disabilityType: nextTypes.join(', '),
        disabilityOther: nextTypes.includes('Other (please specify)') ? prev.disabilityOther : '',
      };
    });
  };

  const handleDisabilityOtherChange = (value) => {
    setFormData(prev => ({
      ...prev,
      disabilityOther: value,
    }));
  };
  
  const handleCancel = () => {
    if (typeof onCancel === 'function') {
      onCancel(formData);
    } else {
      navigate('/app/members');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasCustomSubmit = typeof onSubmit === 'function';

    try {
      setIsSubmitting(true);

      if (hasCustomSubmit) {
        await onSubmit(formData, { isDraft: false });
      } else {
        const payload = buildCreatePayload({ isDraft: false });
        await membersAPI.create(payload);
        toast.success('Member registered successfully');
        navigate('/app/members');
      }
    } catch (error) {
      console.error('Error submitting member form:', error);

      if (hasCustomSubmit) {
        toast.error(error.response?.data?.message || 'Failed to save member information');
      } else if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.errors || {};
        const messages = Object.values(validationErrors).flat();
        toast.error(messages.join('\n') || 'Validation failed');
      } else {
        toast.error(error.response?.data?.message || 'Failed to register member');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const buildCreatePayload = ({ isDraft }) => {
    const payload = new FormData();

    const fieldMap = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      middle_name: formData.middleName,
      email: formData.email,
      phone: formData.phone,
      alternate_phone: formData.alternatePhone,
      date_of_birth: formData.dateOfBirth,
      gender: formData.gender,
      id_number: formData.idNumber,
      occupation: formData.occupation,
      organization_name: formData.organization,
      organization_type: formData.organizationType,
      position_title: formData.positionTitle,
      years_of_experience: formData.yearsOfExperience,
      city: formData.city,
      region: formData.region,
      postal_code: formData.postalCode,
      country: formData.country,
      work_address: formData.workAddress,
      work_phone: formData.workPhone,
      work_email: formData.workEmail,
      membership_type: formData.membershipType || 'individual',
      id_document_type: formData.idDocumentType,
      membership_status: isDraft ? 'pending' : 'active',
      approval_status: isDraft ? 'draft' : 'approved',
      membership_date: new Date().toISOString().split('T')[0],
      bio: formData.bio,
      skills: formData.skills,
      emergency_contact_name: formData.emergencyContactName,
      emergency_contact_phone: formData.emergencyContactPhone,
      emergency_contact_relationship: formData.emergencyContactRelationship,
      availability: formData.availability,
      volunteer_interest: formData.volunteerInterest ? '1' : '0',
      referral_source: formData.referralSource,
    };

    Object.entries(fieldMap).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        payload.append(key, value);
      }
    });

    const interestsArray = typeof formData.interests === 'string'
      ? formData.interests.split(/[,\n]/).map(item => item.trim()).filter(Boolean)
      : Array.isArray(formData.interests)
        ? formData.interests.filter(Boolean)
        : [];

    payload.append('thematic_areas', JSON.stringify(interestsArray));

    const languagesArray = Array.isArray(formData.languages)
      ? formData.languages.filter(Boolean)
      : typeof formData.languages === 'string' && formData.languages.length
        ? formData.languages.split(',').map(item => item.trim()).filter(Boolean)
        : [];

    payload.append('languages_spoken', JSON.stringify(languagesArray));

    // trainings handled separately if needed

    if (formData.idDocumentFile instanceof File) {
      payload.append('id_document_file', formData.idDocumentFile);
    }

    if (formData.photo instanceof File) {
      payload.append('photo', formData.photo);
    }

    payload.append('has_disability', formData.hasDisability === 'yes' ? '1' : '0');

    if (formData.hasDisability === 'yes' && formData.disabilityType) {
      payload.append('disability_type', formData.disabilityType);
    }

    if (formData.cvFile instanceof File) {
      payload.append('cv_file', formData.cvFile);
    }


    return payload;
  };

  const buildUpdatePayload = (data, { isDraft }) => {
    const payload = new FormData();
    const map = {
      first_name: data.firstName,
      middle_name: data.middleName,
      last_name: data.lastName,
      email: data.email,
      phone: data.phone,
      alternate_phone: data.alternatePhone,
      date_of_birth: data.dateOfBirth,
      gender: data.gender,
      id_number: data.idNumber,
      occupation: data.occupation,
      organization_name: data.organization,
      organization_type: data.organizationType,
      position_title: data.positionTitle,
      years_of_experience: data.yearsOfExperience,
      city: data.city,
      region: data.region,
      postal_code: data.postalCode,
      country: data.country,
      work_address: data.workAddress,
      work_phone: data.workPhone,
      work_email: data.workEmail,
      membership_type: data.membershipType,
      membership_status: isDraft ? 'pending' : (data.membershipStatus || 'active'),
      approval_status: isDraft ? 'draft' : (data.approvalStatus || 'approved'),
      bio: data.bio,
      skills: data.skills,
      emergency_contact_name: data.emergencyContactName,
      emergency_contact_phone: data.emergencyContactPhone,
      emergency_contact_relationship: data.emergencyContactRelationship,
      id_document_type: data.idDocumentType,
      availability: data.availability,
      volunteer_interest: data.volunteerInterest ? '1' : '0',
      referral_source: data.referralSource,
    };

    Object.entries(map).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        payload.append(key, value);
      }
    });

    const interestsArray = typeof data.interests === 'string'
      ? data.interests.split(/[,\n]/).map(item => item.trim()).filter(Boolean)
      : Array.isArray(data.interests)
        ? data.interests.filter(Boolean)
        : [];

    payload.append('thematic_areas', JSON.stringify(interestsArray));

    const languagesArray = Array.isArray(data.languages)
      ? data.languages.filter(Boolean)
      : typeof data.languages === 'string' && data.languages.length
        ? data.languages.split(',').map(item => item.trim()).filter(Boolean)
        : [];

    payload.append('languages_spoken', JSON.stringify(languagesArray));

    // trainings handled via dedicated APIs if applicable

    if (data.photo instanceof File) {
      payload.append('photo', data.photo);
    }
    if (data.idDocumentFile instanceof File) {
      payload.append('id_document_file', data.idDocumentFile);
    }

    payload.append('has_disability', data.hasDisability === 'yes' ? '1' : '0');

    if (data.hasDisability === 'yes' && data.disabilityType) {
      payload.append('disability_type', data.disabilityType);
    }

    if (data.cvFile instanceof File) {
      payload.append('cv_file', data.cvFile);
    } else if (data.cvRemoved) {
      payload.append('remove_cv', '1');
    }

    return payload;
  };

  const renderIdDocumentPreview = () => {
    if (!formData.idDocumentPreview) {
      return null;
    }

    const preview = formData.idDocumentPreview;
    const fileName = formData.idDocumentFile?.name;
    const isHttpLink = typeof preview === 'string' && preview.startsWith('http');
    const isObjectUrl = typeof preview === 'string' && preview.startsWith('blob:');

    if (isHttpLink) {
      return (
        <a
          href={preview}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: theme.colors.primary }}
        >
          View current document
        </a>
      );
    }

    if (isObjectUrl) {
      return (
        <a
          href={preview}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: theme.colors.primary }}
        >
          {fileName || 'View uploaded document'}
        </a>
      );
    }

    return fileName || preview;
  };
  
  const resolveMemberId = () =>
    memberId ?? formData.memberId ?? initialData?.memberId ?? initialData?.id ?? null;

  const handleSaveDraftInternal = async () => {
    try {
      setIsSubmitting(true);
      if (mode === 'create') {
      const payload = buildCreatePayload({ isDraft: true });
      const response = await membersAPI.create(payload);
      const memberId = response.data?.id || response.data?.member?.id;
      if (memberId) {
        navigate(`/app/members/${memberId}/complete`);
      } else {
        navigate('/app/members/draft');
      }
      toast.success('Draft saved successfully');
      } else {
        const updatePayload = buildUpdatePayload(formData, { isDraft: true });
        const targetId = resolveMemberId();
        if (!targetId) {
          throw new Error('Missing member identifier for update.');
        }
        await membersAPI.updateMember(targetId, updatePayload);
        toast.success('Draft updated successfully');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(error.response?.data?.message || 'Failed to save draft');
    } finally {
      setIsSubmitting(false);
    }
  };

  const enableSaveDraft = allowSaveDraft ?? (mode === 'create');
  const draftButtonHandler = secondaryButton?.onClick || (enableSaveDraft ? handleSaveDraftInternal : null);
  const handleDraftClick = () => {
    if (draftButtonHandler === handleSaveDraftInternal) {
      handleSaveDraftInternal();
    } else if (typeof draftButtonHandler === 'function') {
      draftButtonHandler(formData, { isDraft: true });
    } else if (typeof onSubmit === 'function') {
      onSubmit(formData, { isDraft: true });
    }
  };

  return (
    <AddMemberContainer theme={theme}>
      {showHeader && (
        <Header theme={theme}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Button 
              variant="outline" 
              onClick={handleCancel}
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <Title theme={theme}>{computedTitle}</Title>
              <Subtitle theme={theme}>{computedSubtitle}</Subtitle>
            </div>
          </div>
        </Header>
      )}
      
      <form onSubmit={handleSubmit}>
        <Card theme={theme}>
          <CardHeader theme={theme}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <UserPlus size={24} style={{ color: theme.colors.primary }} />
              <h2 style={{ 
                fontSize: theme.typography.fontSize.xl,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colors.text.primary,
                margin: 0
              }}>
                Member Registration Form
              </h2>
            </div>
          </CardHeader>
          
          <CardContent theme={theme}>
            {/* Photo Upload Section */}
            <PhotoSection theme={theme}>
              <input
                type="file"
                name="photo"
                accept="image/*"
                onChange={handleInputChange}
                style={{ display: 'none' }}
                id="photo-upload"
                ref={photoInputRef}
              />
              <h3 style={{ 
                fontSize: theme.typography.fontSize.lg,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colors.text.primary,
                margin: 0
              }}>
                Member Photo
              </h3>
              
              {formData.photoPreview ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ position: 'relative' }}>
                    <PhotoPreview theme={theme}>
                      <img src={formData.photoPreview} alt="Member photo" />
                    </PhotoPreview>
                    <RemovePhotoButton theme={theme} onClick={removePhoto} type="button">
                      <X size={16} />
                    </RemovePhotoButton>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    leftIcon={<Upload size={16} />}
                    onClick={() => photoInputRef.current?.click()}
                  >
                    Change Photo
                  </Button>
                </div>
              ) : (
                <FileUpload theme={theme}>
                  <label htmlFor="photo-upload">
                    <Camera size={32} style={{ marginBottom: '0.5rem', color: theme.colors.primary }} />
                    <div>Click to upload photo</div>
                    <div style={{ fontSize: theme.typography.fontSize.sm, opacity: 0.7 }}>
                      PNG, JPG up to 5MB
                    </div>
                  </label>
                </FileUpload>
              )}
            </PhotoSection>
            
            {/* Personal Information */}
            <SectionTitle theme={theme}>
              <UserPlus size={20} />
              Personal Information
            </SectionTitle>
            
            <FormGrid theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>First Name *</Label>
                <Input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter first name"
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Last Name *</Label>
                <Input
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter last name"
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Email Address *</Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Phone Number *</Label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Date of Birth *</Label>
                <Input
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Age</Label>
                <Input
                  name="age"
                  type="number"
                  min="0"
                  value={formData.age}
                  onChange={handleInputChange}
                  placeholder="Enter age in years"
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Gender *</Label>
                <Select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </Select>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Membership Type *</Label>
                <Select
                  name="membershipType"
                  value={formData.membershipType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select membership type</option>
                  {membershipTypes.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Nationality *</Label>
                <Input
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  placeholder="Enter nationality"
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>ID Number</Label>
                <Input
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleInputChange}
                  placeholder="Enter ID number"
                />
              </FormGroup>
            </FormGrid>

            <SectionTitle theme={theme}>
              <FileText size={20} />
              Identification Document
            </SectionTitle>

            <FormGrid theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Document Type *</Label>
                <Select
                  name="idDocumentType"
                  value={formData.idDocumentType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select document type</option>
                  <option value="national_id">National ID</option>
                  <option value="passport">Passport</option>
                  <option value="government_id">Government ID</option>
                </Select>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Upload Document *</Label>
                <input
                  type="file"
                  name="idDocumentFile"
                  accept="image/*,.pdf"
                  onChange={handleInputChange}
                  style={{ display: 'none' }}
                  id="id-document-upload"
                  ref={idDocumentInputRef}
                />
                {formData.idDocumentPreview ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div
                      style={{
                        position: 'relative',
                        padding: '0.75rem',
                        background: theme.colors.background.secondary,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm
                      }}
                    >
                      <div style={{ paddingRight: '2.25rem' }}>
                        {renderIdDocumentPreview()}
                      </div>
                      <RemovePhotoButton theme={theme} type="button" onClick={removeIdDocument}>
                        <X size={16} />
                      </RemovePhotoButton>
                    </div>
                    <div>
                      <Button
                        type="button"
                        variant="secondary"
                        leftIcon={<Upload size={16} />}
                        onClick={() => idDocumentInputRef.current?.click()}
                      >
                        Replace Document
                      </Button>
                    </div>
                  </div>
                ) : (
                  <FileUpload theme={theme}>
                    <label htmlFor="id-document-upload">
                      <Upload size={28} style={{ marginBottom: '0.5rem', color: theme.colors.primary }} />
                      <div>Click to upload ID document</div>
                      <div style={{ fontSize: theme.typography.fontSize.sm, opacity: 0.7 }}>
                        PDF or Image files up to 10MB
                      </div>
                    </label>
                  </FileUpload>
                )}
              </FormGroup>
            </FormGrid>
            <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, marginTop: '0.5rem' }}>
              Please upload a clear copy of the selected identification document.
            </p>
            
            {/* Address Information */}
            <SectionTitle theme={theme}>
              <MapPin size={20} />
              Address Information
            </SectionTitle>
            
            <FormGrid theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>City *</Label>
                <Input
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Region *</Label>
                <Select
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Region</option>
                  {regions.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Region / City (Application Form)</Label>
                <Select
                  name="regionCity"
                  value={formData.regionCity}
                  onChange={handleInputChange}
                >
                  <option value="">Select Region / City</option>
                  {regions.map(region => (
                    <option key={`region-city-${region}`} value={region}>{region}</option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Postal Code</Label>
                <Input
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  placeholder="Enter postal code"
                />
              </FormGroup>
            </FormGrid>
            
            {/* Professional Information */}
            <SectionTitle theme={theme}>
              <GraduationCap size={20} />
              Professional Information
            </SectionTitle>
            
            <FormGrid theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Occupation *</Label>
                <Input
                  name="occupation"
                  value={formData.occupation}
                  onChange={handleInputChange}
                  placeholder="Enter occupation"
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Organization</Label>
                <Input
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  placeholder="Enter organization name"
                />
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Sector *</Label>
                <Select
                  name="organizationType"
                  value={formData.organizationType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select sector</option>
                  {sectorOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Select>
              </FormGroup>

              {formData.organizationType === 'Other' && (
                <FormGroup theme={theme}>
                  <Label theme={theme}>Specify Sector</Label>
                  <Input
                    name="sectorOther"
                    value={formData.sectorOther}
                    onChange={handleInputChange}
                    placeholder="Describe the sector"
                  />
                </FormGroup>
              )}
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Work Address</Label>
                <Input
                  name="workAddress"
                  value={formData.workAddress}
                  onChange={handleInputChange}
                  placeholder="Enter work address"
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Work Phone</Label>
                <Input
                  name="workPhone"
                  value={formData.workPhone}
                  onChange={handleInputChange}
                  placeholder="Enter work phone"
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Work Email</Label>
                <Input
                  name="workEmail"
                  type="email"
                  value={formData.workEmail}
                  onChange={handleInputChange}
                  placeholder="Enter work email"
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Years of Experience</Label>
                <Input
                  name="yearsOfExperience"
                  type="number"
                  value={formData.yearsOfExperience}
                  onChange={handleInputChange}
                  placeholder="Enter years of experience"
                />
              </FormGroup>
            </FormGrid>
            
            {/* Training Interests */}
            <SectionTitle theme={theme}>
              <Award size={20} />
              Training Interests
            </SectionTitle>
            
            <TrainingSection theme={theme}>
              <p style={{ 
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.text.secondary,
                marginBottom: '1rem'
              }}>
                Select training programs you are interested in:
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                {availableTrainings.map(training => (
                  <label key={training} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.trainings.includes(training)}
                      onChange={() => {
                        if (formData.trainings.includes(training)) {
                          removeTraining(training);
                        } else {
                          addTraining(training);
                        }
                      }}
                    />
                    <span style={{ fontSize: theme.typography.fontSize.sm }}>{training}</span>
                  </label>
                ))}
              </div>
              
              {formData.trainings.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ 
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.text.primary,
                    marginBottom: '0.5rem'
                  }}>
                    Selected Trainings:
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {formData.trainings.map(training => (
                      <span key={training} style={{
                        background: theme.colors.primary + '20',
                        color: theme.colors.primary,
                        padding: '0.25rem 0.5rem',
                        borderRadius: theme.borderRadius.sm,
                        fontSize: theme.typography.fontSize.xs,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}>
                        {training}
                        <button
                          type="button"
                          onClick={() => removeTraining(training)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: theme.colors.primary,
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </TrainingSection>
            
            {/* Additional Information */}
            <SectionTitle theme={theme}>
              <FileText size={20} />
              Additional Information
            </SectionTitle>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Bio/Background</Label>
              <TextArea
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                placeholder="Tell us about yourself, your background, and your interest in human rights work..."
                style={{ minHeight: '120px' }}
              />
            </FormGroup>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Areas of Interest</Label>
              <TextArea
                name="interests"
                value={formData.interests}
                onChange={handleInputChange}
                placeholder="Describe your focus areas, thematic interests, or regions you wish to support..."
                style={{ minHeight: '100px' }}
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Additional Details</Label>
              <TextArea
                name="additionalField"
                value={formData.additionalField}
                onChange={handleInputChange}
                placeholder="Provide any additional information requested on the application form."
                style={{ minHeight: '100px' }}
              />
            </FormGroup>

            <SectionTitle theme={theme}>
              <FileText size={20} />
              Motivation & Commitment
            </SectionTitle>

            <FormGroup theme={theme}>
              <Label theme={theme}>Interest in Ethiopia WHRDN</Label>
              <TextArea
                name="interestInEwhrdn"
                value={formData.interestInEwhrdn}
                onChange={handleInputChange}
                placeholder="Describe why you would like to join and support the network."
                style={{ minHeight: '120px' }}
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Women's Rights Issues You Work On</Label>
              <TextArea
                name="womensRightsIssues"
                value={formData.womensRightsIssues}
                onChange={handleInputChange}
                placeholder="Share the women’s rights or gender justice issues you engage with."
                style={{ minHeight: '120px' }}
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Challenges to Gender Equality You Observe</Label>
              <TextArea
                name="genderEqualityChallenges"
                value={formData.genderEqualityChallenges}
                onChange={handleInputChange}
                placeholder="Describe key challenges that need attention."
                style={{ minHeight: '120px' }}
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Short Biography (for publications)</Label>
              <TextArea
                name="shortBiography"
                value={formData.shortBiography}
                onChange={handleInputChange}
                placeholder="Provide a short biography that can be used for directories or certificates."
                style={{ minHeight: '100px' }}
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.politicalAffiliationDeclaration}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    politicalAffiliationDeclaration: e.target.checked,
                  }))}
                />
                <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.primary }}>
                  {POLITICAL_DECLARATION_TEXT}
                </span>
              </label>
            </FormGroup>

            <FormGrid theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Signature (Full Name)</Label>
                <Input
                  name="signatureFullName"
                  value={formData.signatureFullName}
                  onChange={handleInputChange}
                  placeholder="Type your full name as a signature"
                />
              </FormGroup>
              <FormGroup theme={theme}>
                <Label theme={theme}>Confirm Email Address</Label>
                <Input
                  name="emailConfirmation"
                  type="email"
                  value={formData.emailConfirmation}
                  onChange={handleInputChange}
                  placeholder="Re-enter email address"
                />
              </FormGroup>
            </FormGrid>

            <FormGroup theme={theme}>
              <Label theme={theme}>Languages Spoken</Label>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <Input
                  name="languageInput"
                  value={languageInput}
                  onChange={(e) => setLanguageInput(e.target.value)}
                  onKeyDown={handleLanguageKeyDown}
                  placeholder="Type a language and press Enter"
                />
                <Button
                  type="button"
                  variant="secondary"
                  leftIcon={<Plus size={16} />}
                  onClick={handleLanguageAdd}
                  disabled={!languageInput.trim()}
                >
                  Add
                </Button>
              </div>
              {formData.languages.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
                  {formData.languages.map(language => (
                    <span
                      key={language}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        background: theme.colors.primary + '20',
                        color: theme.colors.primary,
                        padding: '0.35rem 0.65rem',
                        borderRadius: theme.borderRadius.sm,
                        fontSize: theme.typography.fontSize.xs,
                        fontWeight: theme.typography.fontWeight.medium,
                      }}
                    >
                      {language}
                      <button
                        type="button"
                        onClick={() => removeLanguage(language)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: theme.colors.primary,
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        aria-label={`Remove ${language}`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.xs, marginTop: '0.5rem' }}>
                  Add the languages the applicant can speak or write. Press Enter after each language.
                </p>
              )}
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Do you have a physical disability?</Label>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="hasDisability"
                    value="no"
                    checked={formData.hasDisability === 'no'}
                    onChange={handleInputChange}
                  />
                  <span>No</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="hasDisability"
                    value="yes"
                    checked={formData.hasDisability === 'yes'}
                    onChange={handleInputChange}
                  />
                  <span>Yes</span>
                </label>
              </div>
            </FormGroup>

            {formData.hasDisability === 'yes' && (
              <>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Disability Type(s)</Label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
                    {disabilityOptions.map(option => (
                      <label key={option} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.65rem', border: `1px solid ${formData.disabilityTypes.includes(option) ? theme.colors.primary : theme.colors.border.primary}`, borderRadius: theme.borderRadius.sm, background: formData.disabilityTypes.includes(option) ? theme.colors.primary + '10' : theme.colors.background.secondary, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.disabilityTypes.includes(option)}
                          onChange={() => toggleDisabilityType(option)}
                        />
                        <span style={{ fontSize: theme.typography.fontSize.sm }}>{option}</span>
                      </label>
                    ))}
                  </div>
                </FormGroup>
                {formData.disabilityTypes.includes('Other (please specify)') && (
                  <FormGroup theme={theme}>
                    <Label theme={theme}>Disability Details</Label>
                    <Input
                      name="disabilityOther"
                      value={formData.disabilityOther}
                      onChange={(event) => handleDisabilityOtherChange(event.target.value)}
                      placeholder="Please specify the disability"
                    />
                  </FormGroup>
                )}
              </>
            )}

            <FormGroup theme={theme}>
              <Label theme={theme}>Upload CV / Resume</Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                <input
                  ref={cvInputRef}
                  type="file"
                  name="cvFile"
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={handleInputChange}
                />
                <Button
                  type="button"
                  variant="secondary"
                  leftIcon={<Upload size={16} />}
                  onClick={() => cvInputRef.current?.click()}
                >
                  {formData.cvFile || formData.cvPreview ? 'Change File' : 'Upload CV'}
                </Button>
                {formData.cvPreview && (
                  <a
                    href={formData.cvPreview}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: theme.colors.primary, fontWeight: 500 }}
                  >
                    View current CV
                  </a>
                )}
                {formData.cvFileName && (
                  <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                    {formData.cvFileName}
                  </span>
                )}
                {(formData.cvPreview || formData.cvFileName) && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={removeCv}
                  >
                    Remove File
                  </Button>
                )}
              </div>
              <small style={{ display: 'block', marginTop: '0.5rem', color: theme.colors.text.secondary }}>
                Accepted formats: PDF, DOC, DOCX (max 5MB)
              </small>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Skills & Expertise</Label>
              <Input
                name="skills"
                value={formData.skills}
                onChange={handleInputChange}
                placeholder="List your skills and areas of expertise (separated by commas)"
              />
            </FormGroup>
            
            {/* Emergency Contact */}
            <SectionTitle theme={theme}>
              <Phone size={20} />
              Emergency Contact
            </SectionTitle>
            
            <FormGrid theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Emergency Contact Name</Label>
                <Input
                  name="emergencyContactName"
                  value={formData.emergencyContactName}
                  onChange={handleInputChange}
                  placeholder="Enter emergency contact name"
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Emergency Contact Phone</Label>
                <Input
                  name="emergencyContactPhone"
                  value={formData.emergencyContactPhone}
                  onChange={handleInputChange}
                  placeholder="Enter emergency contact phone"
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Relationship</Label>
                <Input
                  name="emergencyContactRelationship"
                  value={formData.emergencyContactRelationship}
                  onChange={handleInputChange}
                  placeholder="Enter relationship (e.g., Spouse, Parent, Sibling)"
                />
              </FormGroup>
            </FormGrid>
            
            {/* Form Actions */}
            <FormActions theme={theme}>
              <Button 
                type="button" 
                variant="outline"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              {(secondaryButton || enableSaveDraft) && (
                <Button
                  type="button"
                  variant={secondaryButton?.variant || 'secondary'}
                  onClick={handleDraftClick}
                  disabled={secondaryButton?.disabled || isSubmitting}
                >
                  {secondaryButton?.loading
                    ? (secondaryButton.loadingLabel || secondaryButton.label || 'Saving...')
                    : (secondaryButton?.label || 'Save Draft')}
                </Button>
              )}
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  'Saving...'
                ) : (
                  <>
                    {mode === 'create' ? <UserPlus size={20} /> : <FileText size={20} />}
                    <span style={{ marginLeft: '0.5rem' }}>
                      {submitLabel || (mode === 'create' ? 'Register Member' : 'Save Changes')}
                    </span>
                  </>
                )}
              </Button>
            </FormActions>
          </CardContent>
        </Card>
      </form>
    </AddMemberContainer>
  );
};

export default AddMember;